// src/pages/mission/AfterAction.jsx
// Function:

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  Award, XCircle, TrendingUp, DollarSign, Target, 
  Heart, Skull, ChevronRight, Trophy, Star, AlertTriangle
} from 'lucide-react';
import { THEMES, getColorClasses } from '../themeConfig';

const ThemedAfterAction = ({ theme = 'vanilla' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  
  // Use theme from props, params, location.state, or default
  // FIXED: Added location.state?.theme to the fallback chain
  const activeTheme = theme || params.themeName || location.state?.theme || 'vanilla';
  const config = THEMES[activeTheme] || THEMES.vanilla;
  const primaryColor = config.colors.primary;
  const alertColor = config.alert.color;
  
  const { mission, campaignId, victory, casualties, turns, rewards } = location.state || {};
  
  // Default values if no data passed
  const defaultMission = { name: 'Unknown Mission', type: 'combat' };
  const defaultCasualties = [];
  const defaultRewards = { funds: 0, intel: 0, xp: 0 };

  const activeMission = mission || defaultMission;
  const activeCasualties = casualties || defaultCasualties;
  const activeRewards = rewards || defaultRewards;
  
  const [animatedRewards, setAnimatedRewards] = useState({ funds: 0, intel: 0, xp: 0 });
  const [showDetails, setShowDetails] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('AfterAction - Location State:', location.state);
    console.log('AfterAction - Campaign:', location.state?.campaign);
    console.log('AfterAction - Campaign ID:', campaignId);
    console.log('AfterAction - Theme:', activeTheme);
    console.log('AfterAction - Casualties:', casualties);
    
    // Safety check - if no campaign object, we can't return properly
    if (!location.state?.campaign) {
      console.error('WARNING: No campaign object in state! Navigation may fail.');
    }
  }, [location.state, campaignId, casualties, activeTheme]);

  // Animate reward numbers counting up
  useEffect(() => {
    const duration = 1500;
    const steps = 30;
    const interval = duration / steps;
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      
      setAnimatedRewards({
        funds: Math.floor(activeRewards.funds * progress),
        intel: Math.floor(activeRewards.intel * progress),
        xp: Math.floor(activeRewards.xp * progress)
      });
      
      if (step >= steps) {
        clearInterval(timer);
        setAnimatedRewards(activeRewards);
        setTimeout(() => setShowDetails(true), 300);
      }
    }, interval);
    
    return () => clearInterval(timer);
  }, [activeRewards]);

  const returnToBase = async () => {
    // TODO: Save mission results to backend
    // await api.completeMission(campaignId, mission.id, {
    //   victory,
    //   casualties: casualties.filter(c => !c.survived),
    //   rewards,
    //   turns
    // });

    // Only send survivors back - dead soldiers are removed from squad
    const survivors = activeCasualties.filter(c => c.survived);
    const deadSoldiers = activeCasualties.filter(c => !c.survived);

    console.log('Returning to base with state:', {
      missionComplete: true,
      victory,
      rewards,
      survivors,
      deadSoldiers,
      campaign: location.state?.campaign,
      theme: activeTheme
    });

    // IMPORTANT: Pass the complete campaign object back to Base
    // Base.jsx's loadCampaign expects location.state.campaign to be the full object
    navigate(`/${activeTheme}/base`, { 
      state: { 
        campaign: location.state?.campaign, // Full campaign object - REQUIRED
        missionComplete: true,
        victory,
        rewards,
        casualties: survivors, // Only survivors - dead soldiers removed
        deadSoldiers, // Track separately if needed for UI messages
        tab: 'missions'
      },
      replace: false // Keep in history so back button works
    });
  };

  const survivors = activeCasualties.filter(c => c.survived);
  const kia = activeCasualties.filter(c => !c.survived);

  return (
    <div className={`min-h-screen bg-gradient-to-b ${config.colors.background} text-white`}>
      {/* Background Effect */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 py-12">
        {/* Mission Result Header */}
        <div className={`text-center mb-12 ${victory ? 'animate-fadeIn' : ''}`}>
          {victory ? (
            <>
              <Award className="text-green-400 mx-auto mb-6 animate-bounce" size={80} />
              <h1 className="text-6xl font-bold mb-4 bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
                MISSION SUCCESS
              </h1>
              <p className="text-xl text-gray-300">
                Objectives completed. Outstanding work, Commander.
              </p>
            </>
          ) : (
            <>
              <XCircle className={`${getColorClasses(alertColor)} mx-auto mb-6`} size={80} />
              <h1 className={`text-6xl font-bold mb-4 ${getColorClasses(alertColor)}`}>
                MISSION FAILED
              </h1>
              <p className="text-xl text-gray-300">
                Objectives not met. Regroup and reassess.
              </p>
            </>
          )}
          <div className="mt-4 inline-block bg-gray-800 border border-gray-700 rounded-lg px-6 py-3">
            <p className="text-sm text-gray-400">OPERATION</p>
            <p className="text-xl font-bold">{activeMission.name}</p>
          </div>
        </div>

        {/* Rewards Section */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className={`bg-gray-800 border-2 ${victory ? 'border-green-500' : 'border-gray-700'} rounded-lg p-6 text-center transition-all duration-500 transform ${showDetails ? 'scale-100' : 'scale-95'}`}>
            <DollarSign className="text-green-400 mx-auto mb-3" size={40} />
            <p className="text-sm text-gray-400 mb-2">FUNDS EARNED</p>
            <p className="text-4xl font-bold text-green-400">
              ${animatedRewards.funds?.toLocaleString()}
            </p>
          </div>
          
          <div className={`bg-gray-800 border-2 ${victory ? `${getColorClasses(primaryColor, 'border')}` : 'border-gray-700'} rounded-lg p-6 text-center transition-all duration-500 transform ${showDetails ? 'scale-100' : 'scale-95'}`}>
            <Target className={getColorClasses(primaryColor)} size={40} />
            <p className="text-sm text-gray-400 mb-2">INTEL GATHERED</p>
            <p className={`text-4xl font-bold ${getColorClasses(primaryColor)}`}>
              +{animatedRewards.intel}
            </p>
          </div>
          
          <div className={`bg-gray-800 border-2 ${victory ? 'border-purple-500' : 'border-gray-700'} rounded-lg p-6 text-center transition-all duration-500 transform ${showDetails ? 'scale-100' : 'scale-95'}`}>
            <Star className="text-purple-400 mx-auto mb-3" size={40} />
            <p className="text-sm text-gray-400 mb-2">EXPERIENCE</p>
            <p className="text-4xl font-bold text-purple-400">
              +{animatedRewards.xp} XP
            </p>
          </div>
        </div>

        {/* Mission Statistics */}
        {showDetails && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mb-8 animate-fadeIn">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Trophy className="text-yellow-400" size={24} />
              Mission Statistics
            </h2>
            
            <div className="grid grid-cols-4 gap-6">
              <div className="text-center">
                <div className="bg-gray-900 rounded-lg p-4 mb-2">
                  <p className={`text-3xl font-bold ${getColorClasses(primaryColor)}`}>{turns}</p>
                </div>
                <p className="text-sm text-gray-400">Turns Elapsed</p>
              </div>
              
              <div className="text-center">
                <div className="bg-gray-900 rounded-lg p-4 mb-2">
                  <p className="text-3xl font-bold text-green-400">{survivors.length}</p>
                </div>
                <p className="text-sm text-gray-400">Survivors</p>
              </div>
              
              <div className="text-center">
                <div className="bg-gray-900 rounded-lg p-4 mb-2">
                  <p className={`text-3xl font-bold ${getColorClasses(alertColor)}`}>{kia.length}</p>
                </div>
                <p className="text-sm text-gray-400">Casualties</p>
              </div>
              
              <div className="text-center">
                <div className="bg-gray-900 rounded-lg p-4 mb-2">
                  <p className="text-3xl font-bold text-yellow-400">
                    {survivors.length > 0 ? Math.round((survivors.length / activeCasualties.length) * 100) : 0}%
                  </p>
                </div>
                <p className="text-sm text-gray-400">Survival Rate</p>
              </div>
            </div>
          </div>
        )}

        {/* Squad Status */}
        {showDetails && activeCasualties.length > 0 && (
          <div className="grid grid-cols-2 gap-6 mb-8 animate-fadeIn">
            {/* Survivors */}
            <div className="bg-gray-800 border border-green-500 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Heart className="text-green-400" size={20} />
                Survivors ({survivors.length})
              </h3>
              <div className="space-y-3">
                {survivors.length > 0 ? (
                  survivors.map(soldier => (
                    <div key={soldier.id} className="bg-gray-900/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-bold">{soldier.name}</p>
                          <p className="text-sm text-gray-400">{soldier.rank}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-bold">SURVIVED</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-700 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-green-500"
                            style={{ width: `${(soldier.finalHp / 5) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400">{soldier.finalHp}/5 HP</span>
                      </div>
                      {victory && (
                        <div className="mt-2 pt-2 border-t border-gray-700">
                          <p className="text-xs text-purple-400">+{soldier.experienceGained} XP ({soldier.kills} kills)</p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-8">No survivors</p>
                )}
              </div>
            </div>

            {/* Casualties */}
            <div className={`bg-gray-800 border ${getColorClasses(alertColor, 'border')} rounded-lg p-6`}>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Skull className={getColorClasses(alertColor)} size={20} />
                Casualties ({kia.length})
              </h3>
              <div className="space-y-3">
                {kia.length > 0 ? (
                  kia.map(soldier => (
                    <div key={soldier.id} className="bg-gray-900/50 rounded-lg p-4 opacity-75">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-bold text-gray-300">{soldier.name}</p>
                          <p className="text-sm text-gray-500">{soldier.rank}</p>
                        </div>
                        <div className="text-right">
                          <p className={`${getColorClasses(alertColor)} font-bold`}>K.I.A.</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-700 rounded-full h-2">
                          <div className={`h-2 rounded-full ${getColorClasses(alertColor, 'bgMain')}`} style={{ width: '0%' }} />
                        </div>
                        <span className="text-xs text-gray-500">0/5 HP</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-green-400 font-bold mb-2">FLAWLESS VICTORY</p>
                    <p className="text-sm text-gray-400">No casualties sustained</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Performance Rating */}
        {showDetails && victory && (
          <div className="bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border border-yellow-500 rounded-lg p-6 mb-8 animate-fadeIn">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="text-yellow-400" size={24} />
              Performance Rating
            </h3>
            <div className="flex items-center gap-6">
              <div className="text-6xl font-bold">
                {kia.length === 0 ? (
                  <span className="text-yellow-400">S</span>
                ) : kia.length === 1 ? (
                  <span className="text-green-400">A</span>
                ) : kia.length === 2 ? (
                  <span className={getColorClasses(primaryColor)}>B</span>
                ) : (
                  <span className="text-gray-400">C</span>
                )}
              </div>
              <div className="flex-1">
                <p className="text-lg mb-2">
                  {kia.length === 0 ? (
                    <span className="text-yellow-400 font-bold">PERFECT - No casualties</span>
                  ) : kia.length === 1 ? (
                    <span className="text-green-400 font-bold">EXCELLENT - Minimal losses</span>
                  ) : kia.length === 2 ? (
                    <span className={`${getColorClasses(primaryColor)} font-bold`}>GOOD - Mission success maintained</span>
                  ) : (
                    <span className="text-gray-400 font-bold">ADEQUATE - Heavy casualties</span>
                  )}
                </p>
                <p className="text-sm text-gray-400">
                  Mission completed in {turns} turns with {((survivors.length / activeCasualties.length) * 100).toFixed(0)}% squad survival
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Warning for Failed Mission */}
        {!victory && showDetails && (
          <div className={`bg-red-900/20 border ${getColorClasses(alertColor, 'border')} rounded-lg p-6 mb-8 animate-fadeIn`}>
            <div className="flex items-start gap-4">
              <AlertTriangle className={getColorClasses(alertColor)} size={32} />
              <div>
                <h3 className={`text-xl font-bold ${getColorClasses(alertColor)} mb-2`}>Mission Analysis</h3>
                <p className="text-gray-300 mb-3">
                  The mission did not meet its objectives. All soldiers were eliminated before completing the mission goals.
                </p>
                <p className="text-sm text-gray-400">
                  Consider recruiting additional soldiers, improving equipment, or selecting missions more carefully based on your squad's capabilities.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Return to Base Button */}
        <div className="text-center">
          <button
            onClick={returnToBase}
            className={`group relative ${
              victory 
                ? `${getColorClasses(primaryColor, 'bgMain')} ${getColorClasses(primaryColor, 'bgHover')}` 
                : 'bg-gray-700 hover:bg-gray-600'
            } text-white font-bold px-12 py-4 rounded-lg text-xl transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center gap-3 mx-auto`}
          >
            <span className="relative z-10">RETURN TO BASE</span>
            <ChevronRight size={28} className="relative z-10" />
            {victory && (
              <div className={`absolute inset-0 ${getColorClasses(primaryColor, 'bgMain')} rounded-lg blur opacity-50 group-hover:opacity-75 transition-opacity`} />
            )}
          </button>
          <p className="mt-4 text-sm text-gray-500">
            Mission data will be saved to campaign progress
          </p>
        </div>
      </div>
    </div>
  );
};

// Named exports for specific themes
export const VanillaAfterAction = (props) => <ThemedAfterAction theme="vanilla" {...props} />;
export const MajesticAfterAction = (props) => <ThemedAfterAction theme="majestic" {...props} />;
export const DevastatorsAfterAction = (props) => <ThemedAfterAction theme="devastators" {...props} />;
export const ArchboundAfterAction = (props) => <ThemedAfterAction theme="archbound" {...props} />;

export default ThemedAfterAction;